import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { applyConsent, onOpenCookieSettings, readConsent, saveConsent } from "../lib/consent";
import { GA_MEASUREMENT_ID, HAS_TRACKERS, META_PIXEL_ID } from "../lib/siteConfig";

/**
 * Bandeau cookies + fenêtre « Gérer les cookies ».
 * - « Tout refuser » et « Tout accepter » ont exactement le même style (exigence CNIL).
 * - Rouvrable à tout moment via openCookieSettings() (lien du pied de page).
 */
function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative w-10 h-6 shrink-0 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-white/15"} ${
        disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
    >
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
    </button>
  );
}

const neutralBtn = "h-10 px-5 rounded-md text-sm font-medium border border-white/15 bg-white/[0.06] hover:bg-white/10 transition-colors";

// Aucun outil de mesure configuré (siteConfig.js) : seuls des cookies nécessaires → pas de bandeau
export default function CookieConsent() {
  return HAS_TRACKERS ? <CookieConsentBanner /> : null;
}
function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, ads: false });

  useEffect(() => {
    const existing = readConsent();
    if (existing) {
      setPrefs({ analytics: existing.analytics, ads: existing.ads });
      applyConsent(existing);
    } else {
      setShowBanner(true);
    }
    return onOpenCookieSettings(() => {
      const c = readConsent();
      setPrefs({ analytics: !!c?.analytics, ads: !!c?.ads });
      setShowPrefs(true);
    });
  }, []);

  const decide = (choice) => {
    setPrefs(choice);
    setShowBanner(false);
    setShowPrefs(false);
    saveConsent(choice);
  };

  if (!showBanner && !showPrefs) return null;

  if (showPrefs) {
    const rows = [
      ["necessary", "Nécessaires", "Connexion à votre compte, sécurité, mémorisation de vos choix. Indispensables au fonctionnement du site.", true, true],
      GA_MEASUREMENT_ID && ["analytics", "Mesure d'audience", "Google Analytics : statistiques de fréquentation pour améliorer le site.", prefs.analytics, false],
      META_PIXEL_ID && ["ads", "Publicité", "Meta Pixel : mesure de l'efficacité de nos publicités sur Instagram et Facebook.", prefs.ads, false],
    ].filter(Boolean);
    return (
      <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="cookie-preferences">
        <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[#121214] shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="cookie-prefs-title">
          <div className="flex items-center justify-between px-6 pt-5">
            <h2 id="cookie-prefs-title" className="font-display font-bold text-lg">Gérer les cookies</h2>
            <button
              type="button"
              onClick={() => {
                setShowPrefs(false);
                if (!readConsent()) setShowBanner(true); // aucun choix fait : le bandeau revient
              }}
              className="text-zinc-500 hover:text-white"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-6 pb-2 mt-2">
            {rows.map(([key, title, desc, on, locked]) => (
              <div key={key} className="flex items-start justify-between gap-4 py-4 border-t border-white/5 first:border-0">
                <div>
                  <div className="text-sm font-medium text-zinc-100">
                    {title}
                    {locked && <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">Toujours actifs</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500 leading-relaxed">{desc}</p>
                </div>
                <Toggle checked={on} disabled={locked} label={title} onChange={(val) => setPrefs((p) => ({ ...p, [key]: val }))} />
              </div>
            ))}
          </div>
          <div className="px-6 py-4 border-t border-white/5 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <button type="button" className={neutralBtn} onClick={() => decide({ analytics: false, ads: false })} data-testid="cookie-refuse-all">
              Tout refuser
            </button>
            <button type="button" className={neutralBtn} onClick={() => decide({ analytics: true, ads: true })} data-testid="cookie-accept-all">
              Tout accepter
            </button>
            <button
              type="button"
              className="h-10 px-4 rounded-md text-sm font-medium bg-emerald-500 text-black hover:bg-emerald-300"
              onClick={() => decide(prefs)}
              data-testid="cookie-save"
            >
              Enregistrer mes choix
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-16 md:bottom-0 z-[60] p-4 sm:p-6" data-testid="cookie-banner">
      <div className="mx-auto max-w-3xl rounded-xl border border-white/10 bg-[#121214]/95 backdrop-blur shadow-2xl p-5 sm:p-6" role="dialog" aria-label="Cookies">
        <div className="flex gap-3">
          <Cookie className="w-5 h-5 text-zinc-300 mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-bold text-lg">Votre vie privée</div>
            <p className="mt-1 text-sm text-zinc-400 leading-relaxed">
              Nous utilisons des cookies nécessaires au fonctionnement du site. Avec votre accord, nous utilisons aussi des cookies
              de mesure d'audience (Google Analytics) et publicitaires (Meta Pixel). Vous pouvez changer d'avis à tout moment via
              « Gérer les cookies » en bas de page.{" "}
              <Link to="/confidentialite" className="text-zinc-200 underline underline-offset-4">
                En savoir plus
              </Link>
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:justify-end">
          <button
            type="button"
            onClick={() => {
              setShowBanner(false);
              setShowPrefs(true);
            }}
            className="text-sm text-zinc-400 hover:text-white underline-offset-4 hover:underline sm:mr-auto"
            data-testid="cookie-customize"
          >
            Personnaliser
          </button>
          <button type="button" className={neutralBtn} onClick={() => decide({ analytics: false, ads: false })} data-testid="cookie-banner-refuse">
            Tout refuser
          </button>
          <button type="button" className={neutralBtn} onClick={() => decide({ analytics: true, ads: true })} data-testid="cookie-banner-accept">
            Tout accepter
          </button>
        </div>
      </div>
    </div>
  );
}
